import { useState } from "react";
import { postJson } from "../api";
import { formatText, useTexts, type Label, type Locale } from "../texts";
import { Notice, PageHead, useToast } from "../ui/kit";
import { PageState, ServiceState } from "../ui/pieces";
import { connectionTone, plural, routeName, usableOf, when } from "./ai";
import { useAiData, useTitle } from "./parts";
import { Indicator, IndicatorSkeleton, Indicators, Mark, RowsSkeleton, Section } from "./widgets";
import styles from "./ai.module.css";

type Config = {
  routes: {
    routeCode: string;
    state: string;
    modelName: string | null;
    providerName: string | null;
    backups: number;
    preparing: number | null;
  }[];
  providers: {
    providerCode: string;
    providerName: string;
    state: string;
    connectionId: number | null;
    /** «Todos» y «Usables» de la regla única (los mismos de la lista de Proveedores y la ficha). */
    models: number;
    usableModels: number;
    lastSyncedAt: string | null;
  }[];
};

type Provider = Config["providers"][number];

const routeOrder = ["TEXT_CHAT", "VISION_CHAT", "EMBEDDING"];

/**
 * IA › Resumen (§5.9 `ia-resumen`): aviso si una capacidad no tiene modelo, las tres capacidades con su modelo principal
 * (cada una lleva a su pestaña de Rutas) y los proveedores en Conectados, Incompletos y Sin API key con su acción.
 * Un proveedor no se «conecta»: «Agregar API key» abre su ficha con la ventana (decisión del dueño, 2026-10-30).
 * «Actualizar modelos» trae el catálogo del proveedor (D15) y se desactiva mientras trabaja.
 */
export function OverviewPage() {
  const { L, locale } = useTexts();
  const toast = useToast();
  const { data, state, reload, retry } = useAiData<Config>("/api/admin/ai/config");
  const [syncing, setSyncing] = useState<string | null>(null);
  useTitle(L("ADM_AI_SUMMARY"));

  const head = <PageHead title={L("ADM_AI_SUMMARY")} sub={L("ADM_AI_SUMMARY_SUB")} />;
  if (state === "failed" || state === "missing")
    return (
      <PageState
        head={head}
        title={L("ADM_UNAVAILABLE")}
        line={L("ADM_UNAVAILABLE_LINE")}
        action={
          <button type="button" className="tm-btn secondary" onClick={retry}>
            {L("ADM_RETRY")}
          </button>
        }
      />
    );

  const sync = async (provider: Provider) => {
    if (!provider.connectionId || syncing) return;
    setSyncing(provider.providerCode);
    const reply = await postJson<{ failed?: boolean; added?: number; updated?: number }>(
      `/api/admin/ai/connections/${provider.connectionId}/sync`,
      {},
    );
    setSyncing(null);
    if (reply.status === 200 && reply.body && !reply.body.failed)
      toast.show(
        formatText(
          L("ADM_AI_SYNC_DONE"),
          provider.providerName,
          (reply.body.added ?? 0).toLocaleString(locale),
          (reply.body.updated ?? 0).toLocaleString(locale),
        ),
      );
    else toast.show(formatText(L("ADM_AI_SYNC_FAILED"), provider.providerName));
    void reload();
  };

  const routes = data
    ? routeOrder
        .map((code) => data.routes.find((route) => route.routeCode === code))
        .filter((route): route is Config["routes"][number] => !!route)
    : [];
  const empty = routes.filter((route) => route.state !== "CONFIGURED");
  const providers = data?.providers ?? [];
  const connected = providers.filter((item) => item.state === "CONNECTED" || item.state === "CONNECTION_ERROR");
  const incomplete = providers.filter((item) => item.state === "NEEDS_KEY" || item.state === "NEEDS_VERIFICATION");
  const others = providers.filter((item) => !connected.includes(item) && !incomplete.includes(item));

  const row = (provider: Provider, line: string, action: React.ReactNode) => {
    const tone = connectionTone(L, provider.state);
    const href = `/Ai/Providers/${encodeURIComponent(provider.providerCode)}`;
    return (
      <li key={provider.providerCode} className={styles.row}>
        <a className={styles.rowLinkArea} href={href}>
          <Mark name={provider.providerName} />
          <span className={styles.rowText}>
            <span className={styles.line1}>
              <strong>{provider.providerName}</strong>
              <ServiceState tone={tone.tone}>{tone.text}</ServiceState>
            </span>
            <span className={styles.line2}>{line}</span>
          </span>
        </a>
        <span className={styles.rowEnd}>{action}</span>
      </li>
    );
  };
  const models = (provider: Provider) => usableOf(L, locale, provider.usableModels, provider.models);
  // La API key se agrega dentro del proveedor: su ficha abre la ventana.
  const addKey = (provider: Provider) => (
    <a className="tm-btn secondary" href={`/Ai/Providers/${encodeURIComponent(provider.providerCode)}?conectar=1`}>
      {L("ADM_ADD_KEY")}
    </a>
  );

  return (
    <>
      {head}
      {empty.map((route) => (
        <Notice
          key={route.routeCode}
          tone="warning"
          action={
            <a className="tm-btn secondary" href={`/Ai/Rutas?route=${route.routeCode}`}>
              {L("ADM_CHOOSE_MODEL")}
            </a>
          }
        >
          {L(`ADM_AI_EMPTY_${route.routeCode}`)}
        </Notice>
      ))}
      <Section id="ai-capabilities" title={L("ADM_AI_CAPABILITIES")} line={L("ADM_AI_CAPABILITIES_LINE")}>
        {data ? (
          <Indicators label={L("ADM_AI_CAPABILITIES")} count={3}>
            {routes.map((route) => (
              <Indicator
                key={route.routeCode}
                href={`/Ai/Rutas?route=${route.routeCode}`}
                icon={capabilityIcon(route.routeCode)}
                label={routeName(L, route.routeCode)}
                value={route.modelName ?? L("ADM_AI_NO_MODEL")}
                small
                muted={!route.modelName}
                line={capabilityLine(L, locale, route)}
              />
            ))}
          </Indicators>
        ) : (
          <IndicatorSkeleton count={3} label={L("ADM_AI_CAPABILITIES")} />
        )}
      </Section>
      {!data ? (
        <RowsSkeleton rows={4} />
      ) : (
        <>
          {connected.length > 0 ? (
            <Section id="ai-connected" title={L("ADM_AI_CONNECTED")}>
              <ul className={styles.list}>
                {connected.map((provider) =>
                  row(
                    provider,
                    `${models(provider)} · ${
                      provider.lastSyncedAt
                        ? formatText(L("ADM_AI_SYNCED_AT"), when(L, locale, provider.lastSyncedAt, true))
                        : L("ADM_AI_NEVER_SYNCED")
                    }`,
                    provider.state === "CONNECTED" ? (
                      <button
                        type="button"
                        className="tm-btn secondary"
                        disabled={syncing !== null}
                        aria-busy={syncing === provider.providerCode || undefined}
                        onClick={() => void sync(provider)}
                      >
                        {syncing === provider.providerCode ? L("NPA_AI_SYNCING") : L("ADM_AI_SYNC_MODELS")}
                      </button>
                    ) : (
                      <a
                        className="tm-btn secondary"
                        href={`/Ai/Providers/${encodeURIComponent(provider.providerCode)}`}
                      >
                        {L("ADM_REVIEW")}
                      </a>
                    ),
                  ),
                )}
              </ul>
            </Section>
          ) : null}
          {incomplete.length > 0 ? (
            <Section id="ai-incomplete" title={L("ADM_AI_INCOMPLETE")}>
              <ul className={styles.list}>
                {incomplete.map((provider) =>
                  row(
                    provider,
                    L(provider.state === "NEEDS_KEY" ? "ADM_AI_NO_KEY_LINE" : "ADM_AI_UNTESTED_LINE"),
                    provider.state === "NEEDS_KEY" ? (
                      addKey(provider)
                    ) : (
                      <a
                        className="tm-btn secondary"
                        href={`/Ai/Providers/${encodeURIComponent(provider.providerCode)}`}
                      >
                        {L("ADM_REVIEW")}
                      </a>
                    ),
                  ),
                )}
              </ul>
            </Section>
          ) : null}
          {others.length > 0 ? (
            <Section id="ai-others" title={L("ADM_AI_TO_CONNECT")}>
              <ul className={styles.list}>
                {others.map((provider) =>
                  row(
                    provider,
                    models(provider),
                    provider.state === "DISABLED" ? (
                      <a
                        className="tm-btn secondary"
                        href={`/Ai/Providers/${encodeURIComponent(provider.providerCode)}`}
                      >
                        {L("ADM_REVIEW")}
                      </a>
                    ) : (
                      addKey(provider)
                    ),
                  ),
                )}
              </ul>
            </Section>
          ) : null}
          {providers.length === 0 ? (
            <div className="tm-empty" role="status">
              <strong>{L("ADM_AI_NO_PROVIDERS")}</strong>
              <span>{L("ADM_AI_NO_PROVIDERS_LINE")}</span>
            </div>
          ) : null}
        </>
      )}
    </>
  );
}

/** Ícono de cada capacidad en su tarjeta (referencia de diseño): Texto, Visión (ya «eye») y Embeddings. */
function capabilityIcon(code: string): string {
  switch (code) {
    case "TEXT_CHAT":
      return "type";
    case "VISION_CHAT":
      return "eye";
    case "EMBEDDING":
      return "layers";
    default:
      return "";
  }
}

/** Contexto de una capacidad: «OpenAI · 1 respaldo», «OpenAI · preparando índice» o «Elige el principal». */
function capabilityLine(L: Label, locale: Locale, route: Config["routes"][number]): string {
  if (!route.modelName) return L("ADM_AI_CHOOSE_MAIN");
  const parts = [route.providerName ?? L("USAGE_UNKNOWN")];
  if (route.routeCode === "EMBEDDING") {
    if (route.preparing) parts.push(L("ADM_AI_INDEX_PREPARING_SHORT"));
  } else if (route.backups > 0)
    parts.push(plural(L, locale, route.backups, "ADM_AI_N_BACKUPS_ONE", "ADM_AI_N_BACKUPS"));
  return parts.join(" · ");
}
